import { Ref, computed, isRef, ref } from 'vue'
import { useFetchGeneral } from '@/composables/useFetch'
import fetches, { FetchConfig, FetchFilter, FetchQuery, FetchResponse, FetchType } from '@/fetches'
import { FeatureFilters, useCurrentFeatureFilters } from './useFilters'

export type DataFetcher<T> = {
  data: Ref<T | undefined>
  isLoading: Ref<boolean>
  error: Ref<any>
}

export type DataFetcherOptions<R> = {
  useSubmittedFilter?: boolean
  fetchOnlyIf?: (query: R | null) => boolean
}

function withDefaults<R>(options: DataFetcherOptions<R>) {
  return {
    useSubmittedFilter: true,
    fetchOnlyIf: () => true,
    ...options
  }
}

export function useDataFetcher<T extends FetchType, Response extends FetchResponse<T>>(
  id: Ref<T | undefined> | T | undefined,
  options: DataFetcherOptions<FetchQuery<T>> = {
    useSubmittedFilter: true,
    fetchOnlyIf: () => true
  }
) {
  type Request = FetchQuery<T>
  const { useSubmittedFilter, fetchOnlyIf } = withDefaults(options)
  const filters = useCurrentFeatureFilters()
  const submitted = computed(() => filters.value?.submitted)
  const current = computed(() => filters.value?.currentStable)
  const filter: Ref<FeatureFilters<FetchFilter<T>> | undefined> = (
    useSubmittedFilter ? submitted : current
  ) as Ref<FeatureFilters<FetchFilter<T>> | undefined>

  const prevQuery = ref<FetchQuery<T> | null>(null)
  const prevKey = ref<T | undefined>()

  const key = isRef(id) ? id : ref<T | undefined>(id)

  const queryPath = computed<FetchConfig<Request> | undefined>(
    (): FetchConfig<Request> | undefined => {
      if (!key.value || !filter?.value) return undefined
      if (!(key.value in fetches)) {
        console.error(`Unknown fetch id: ${key.value} (not implemented)`)
        return undefined
      }
      const fetch = fetches[key.value]
      return {
        method: 'GET',
        ...fetch(filter.value)
      }
    }
  )

  const urlPath = computed(() => {
    if (!queryPath.value) return undefined
    const { query, path, queryParams } = queryPath.value
    if (!query) {
      prevQuery.value = query
      return undefined
    }
    if (queryPath.value.method === 'POST') return path
    prevQuery.value = query
    prevKey.value = key.value

    const urlQuery = encodeURIComponent(JSON.stringify(query))

    if (queryParams) {
      const params = new URLSearchParams()
      for (const key in queryParams) {
        const value = queryParams[key]
        if (value !== undefined && value !== null) {
          params.append(key, value.toString())
        }
      }
      return `${path}?query=${urlQuery}&${params.toString()}`
    }

    return `${path}?query=${urlQuery}`
  })

  const generalFetch = useFetchGeneral<Request, Response>(
    urlPath,
    computed(() => queryPath.value?.method),
    computed(() => (queryPath.value ? queryPath.value.query : null)),
    fetchOnlyIf
  )

  return {
    ...generalFetch,
    query: computed(() => queryPath.value?.query ?? undefined)
  }
}
